import React from 'react';
import { Box, Button } from '@mui/material';
import { colors, font, layout } from '../theme/tokens';

// ─────────────────────────────────────────────────────────────────────────────
// BODE "green bubble & button" — styleguide, Design assets.
//
// Fully rounded, 'the innovation' lime fill, 'the base' navy label, uppercase
// and letter-spaced. The circular variant is the guide's ← → i bubble.
//
// Navy on lime measures 15.7:1, so the label stays legible at small sizes.
// ─────────────────────────────────────────────────────────────────────────────

type ButtonProps = {
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  fullWidth?: boolean;
  type?: 'button' | 'submit';
  /** Tighter padding for use inside a card. */
  dense?: boolean;
};

export const BubbleButton: React.FC<ButtonProps> = ({
  children, onClick, disabled, fullWidth, dense,
}) => (
  <Button
    onClick={onClick}
    disabled={disabled}
    fullWidth={fullWidth}
    disableRipple
    sx={{
      px: dense ? 2.5 : 4,
      py: dense ? 1 : 1.5,
      minWidth: 0,
      borderRadius: layout.radiusPill,
      backgroundColor: colors.accent,
      color: colors.ink,
      fontFamily: font.sans,
      fontWeight: 600,
      fontSize: dense ? '12px' : '14px',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      boxShadow: 'none',
      transition: 'background-color .15s ease',
      '&:hover': { backgroundColor: colors.accentPress, boxShadow: 'none' },
      '&:disabled': { backgroundColor: colors.border, color: colors.inkGhost },
    }}
  >
    {children}
  </Button>
);

type BubbleProps = {
  children: React.ReactNode;
  size?: number;
  /** Rendered as a plain span — use inside an element that is already clickable. */
  as?: 'span' | 'button';
  onClick?: () => void;
  label?: string;
};

/** Circular lime bubble carrying a single glyph: ← → i */
export const Bubble: React.FC<BubbleProps> = ({
  children, size = 34, as = 'span', onClick, label,
}) => (
  <Box
    component={as}
    onClick={onClick}
    aria-label={label}
    sx={{
      width: size,
      height: size,
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      padding: 0,
      backgroundColor: colors.accent,
      color: colors.ink,
      fontFamily: font.sans,
      fontSize: `${Math.round(size * 0.44)}px`,
      fontWeight: 600,
      lineHeight: 1,
      cursor: as === 'button' ? 'pointer' : 'inherit',
      transition: 'background-color .18s ease, transform .18s ease',
    }}
  >
    {children}
  </Box>
);

export default BubbleButton;
